import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { ErrorState, Skeleton } from '@/components/ui/Feedback'
import { ProgressBar, Section } from '@/components/ui/Page'
import { StatusPill } from '@/components/ui/StatusPill'
import { formatDate, humanise } from '@/lib/utils'
import type { BillingPeriod, SubscriptionResponse, UsageSummary } from '@/types/api'

import {
  FeatureList,
  PERIOD_NOUN,
  PeriodToggle,
  PlanRow,
  RECOMMENDED_PLAN,
  featureRows,
  formatCount,
  planAction,
  subscriptionStatus,
} from './planUi'
import { useBillingPortal, useCheckout, usePlans, useSubscription } from './useLicense'

/** Profile → Subscription: the plan, this month's allowance, billing, history. */
export function SubscriptionTab() {
  const query = useSubscription()
  if (query.isLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-40 w-full rounded-card" />
        <Skeleton className="h-32 w-full rounded-card" />
      </div>
    )
  }
  const data = query.data
  if (!data) {
    return (
      <div className="card">
        <ErrorState title="Subscription unavailable" description="Try again in a moment." onRetry={() => query.refetch()} />
      </div>
    )
  }
  if (!data.subscription) return <NoPlan />
  return <Subscribed data={data} />
}

function Subscribed({ data }: { data: SubscriptionResponse }) {
  const sub = data.subscription!
  const portal = useBillingPortal()
  const status = subscriptionStatus(sub)
  const renewal = sub.cancelAtPeriodEnd
    ? `Ends on ${formatDate(sub.currentPeriodEnd)}, will not renew`
    : sub.renewsAt
      ? `Renews on ${formatDate(sub.renewsAt)}`
      : `Runs until ${formatDate(sub.currentPeriodEnd)}`

  return (
    <div className="space-y-4">
      <Section
        title={
          <span className="flex flex-wrap items-center gap-2">
            {sub.planName}
            <span className="text-[13px] font-normal text-ink-muted">· {PERIOD_NOUN[sub.period]}</span>
          </span>
        }
        actions={<StatusPill tone={status.tone}>{status.label}</StatusPill>}
      >
        {!data.active && (
          <p className="mb-4 rounded-field bg-warning/10 px-3.5 py-3 text-[13.5px] leading-relaxed text-ink-soft" role="status">
            {sub.status === 'past_due' || sub.status === 'incomplete'
              ? 'Your last payment did not go through. Update your card to keep applying.'
              : 'This plan is no longer in force. Choose a plan to apply again.'}
          </p>
        )}
        <dl className="grid gap-x-6 gap-y-3 text-[13.5px] sm:grid-cols-2">
          <Fact label="Started on">{formatDate(sub.startedAt)}</Fact>
          <Fact label="Current term">
            {formatDate(sub.currentPeriodStart)} – {formatDate(sub.currentPeriodEnd)}
          </Fact>
          <Fact label="Renewal">{renewal}</Fact>
          <Fact label="Source">{sub.source === 'stripe' ? 'Paid by card (Stripe)' : 'Granted by an administrator'}</Fact>
        </dl>
        <div className="mt-5 flex flex-col gap-2 xs:flex-row xs:flex-wrap">
          {sub.source === 'stripe' && (
            <Button variant="secondary" onClick={portal.open} loading={portal.pending}>
              Manage billing
            </Button>
          )}
          <Link
            to="/dashboard/plans"
            className="inline-flex h-11 items-center justify-center rounded-field bg-ink px-5 text-[15px] font-medium text-oncolor transition-colors hover:bg-ink/90"
          >
            {data.active ? 'Change plan' : 'Choose a plan'}
          </Link>
        </div>
        {sub.source === 'stripe' && (
          <p className="mt-2 text-[12px] text-ink-faint">Update your card, download invoices or cancel on Stripe's billing page.</p>
        )}
      </Section>

      {data.usage && <UsageCard usage={data.usage} />}

      <Section title="What your plan includes">
        <FeatureList rows={featureRows(data.features)} />
      </Section>

      <Section title="History" bodyClassName="p-0">
        {data.history.length === 0 ? (
          <p className="px-5 py-4 text-[13.5px] text-ink-muted">Nothing yet.</p>
        ) : (
          <ol className="divide-y divide-line">
            {[...data.history]
              .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
              .map((event, index) => (
                <li key={`${event.at}-${index}`} className="flex flex-col gap-0.5 px-5 py-3 sm:flex-row sm:items-baseline sm:gap-4">
                  <span className="shrink-0 text-[13px] font-medium text-ink sm:w-36">{humanise(event.type)}</span>
                  <span className="min-w-0 flex-1 text-[13.5px] leading-relaxed text-ink-soft">{event.message}</span>
                  <time dateTime={event.at} className="num shrink-0 text-[12px] text-ink-faint">
                    {formatDate(event.at)}
                  </time>
                </li>
              ))}
          </ol>
        )}
      </Section>
    </div>
  )
}

function UsageCard({ usage }: { usage: UsageSummary }) {
  const pct = usage.limit ? ((usage.used + usage.inProgress) / usage.limit) * 100 : 0
  const tone = usage.remaining === 0 ? 'danger' : pct >= 85 ? 'warning' : 'brand'
  return (
    <Section title="This month" description={`${formatDate(usage.windowStart)} – ${formatDate(usage.windowEnd)}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[13.5px] text-ink-muted">
          <span className="num text-[26px] font-semibold leading-none text-ink">{formatCount(usage.used)}</span>
          <span className="num"> / {formatCount(usage.limit)}</span> applications sent
        </p>
        <p className="text-[13px] text-ink-muted">Resets on {formatDate(usage.windowEnd)}</p>
      </div>
      <ProgressBar value={pct} tone={tone} label="Applications used this month" className="mt-3" />
      <dl className="mt-4 grid grid-cols-3 divide-x divide-line rounded-field border border-line">
        <Meter label="Sent" value={usage.used} />
        <Meter label="In progress" value={usage.inProgress} />
        <Meter label="Remaining" value={usage.remaining} strong />
      </dl>
    </Section>
  )
}

function Meter({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <div className="min-w-0 px-3 py-2.5">
      <dt className="truncate text-[12px] text-ink-muted">{label}</dt>
      <dd className={`num mt-0.5 text-[18px] font-semibold leading-none ${strong ? 'text-ink' : 'text-ink-soft'}`}>
        {formatCount(value)}
      </dd>
    </div>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[12px] text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  )
}

/** No subscription yet: the three plans, compact, with Choose. */
function NoPlan() {
  const plansQuery = usePlans()
  const checkout = useCheckout()
  const [period, setPeriod] = useState<BillingPeriod>('monthly')
  const plans = plansQuery.data

  return (
    <Section title="No plan yet" description="Choose a plan to start applying. Your profile, matches and the assistant stay free.">
      {plans ? (
        <div className="space-y-3">
          <PeriodToggle data={plans} value={period} onChange={setPeriod} size="sm" />
          <ul className="panel divide-y divide-line overflow-hidden" aria-label="Plans">
            {plans.plans.map((plan) => (
              <PlanRow
                key={plan.key}
                plan={plan}
                period={period}
                current={false}
                recommended={plan.key === RECOMMENDED_PLAN}
                action={planAction({ plan, period, subscription: null, billingEnabled: plans.billingEnabled })}
                pending={checkout.pendingPlan === plan.key}
                onChoose={() => checkout.choose(plan.key, period)}
              />
            ))}
          </ul>
          {!plans.billingEnabled && <p className="text-[12.5px] font-medium text-warning">Payments open soon.</p>}
        </div>
      ) : (
        <Skeleton className="h-40 w-full rounded-card" />
      )}
      <Link
        to="/dashboard/plans"
        className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-field bg-ink px-5 text-[15px] font-medium text-oncolor transition-colors hover:bg-ink/90 xs:w-auto"
      >
        Choose a plan
      </Link>
    </Section>
  )
}
